import React, { useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import * as U from "./UserPageStyle";
import { AuthContext } from "../../context/AuthContext";
import { usePulse } from "../../context/LoadContext";
import { formatCPF, formatCNPJ } from "../../assets/utils";
import { useMessage } from "../../context/MessageContext";
import { clientService } from "../../Services/clientService"; // Importa o serviço

const getClientIp = async () => {
  let publicIp = "";
  try {
    const response = await fetch("https://api.ipify.org?format=json");
    if (!response.ok) {
      console.error("Falha ao obter o IP público do usuário.");
    } else {
      const data = await response.json();
      publicIp = data.ip;
    }
    return publicIp;
  } catch (ipError) {
    console.error("Erro ao tentar buscar o IP público:", ipError);
  }
};

const ProfilePage = ({ setProfilePage }) => {
  const { userData, reloadUserData } = useContext(AuthContext);
  const { showPulse, hidePulse } = usePulse();
  const { showMessage } = useMessage();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState("profile");
  const [isEditing, setIsEditing] = useState(false);
  const [formValues, setFormValues] = useState({});
  const [initialValues, setInitialValues] = useState({});
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    if (userData) {
      const formattedCpfCnpj =
        userData.cpfCnpj?.length === 11
          ? formatCPF(userData.cpfCnpj)
          : formatCNPJ(userData.cpfCnpj);
      const initialData = {
        name: userData.name || "",
        username: userData.username || "",
        email: userData.email || "",
        cpfCnpj: formattedCpfCnpj || "",
        consultorName: userData.consultorName || "",
        phoneNumber: userData.phoneNumber || "",
        jobTitle: userData.jobTitle || "",
        "address.street": userData.address?.street || "",
        "address.number": userData.address?.number || "",
        "address.neighborhood": userData.address?.neighborhood || "",
        "address.city": userData.address?.city || "",
        "address.state": userData.address?.state || "",
        "address.zipcode": userData.address?.zipcode || "",
        "bankAccount.bank": userData.bankAccount?.bank || "",
        "bankAccount.agencyNumber": userData.bankAccount?.agencyNumber || "",
        "bankAccount.accountNumber": userData.bankAccount?.accountNumber || "",
        "bankAccount.accountType":
          userData.bankAccount?.accountType || "Conta Corrente",
        "bankAccount.beneficiary": userData.bankAccount?.beneficiary || "",
        "bankAccount.pixKey": userData.bankAccount?.pixKey || "",
      };
      setFormValues(initialData);
      setInitialValues(initialData);
    }
  }, [userData]);

  const showNotification = (message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => {
      setNotification(null);
    }, 3000);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleCancel = () => {
    setFormValues(initialValues);
    setIsEditing(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    showPulse();

    const changedFields = Object.keys(formValues).filter(
      (key) => formValues[key] !== initialValues[key]
    );

    if (changedFields.length === 0) {
      hidePulse();
      setIsEditing(false);
      showNotification("Nenhuma alteração para salvar.", "info");
      return;
    }

    const patchDtos = changedFields.map((key) => ({
      fieldName: key,
      fieldNewValue: formValues[key],
    }));

    try {
      // 1. Busca o IP (não bloqueia se falhar, retorna null)
      const ipAddress = await getClientIp();

      // 2. Passa o IP como segundo argumento
      await clientService.updateClientData(patchDtos, ipAddress);

      await reloadUserData();
      setIsEditing(false);
      showMessage("Dados atualizados com sucesso!", "success");
    } catch (error) {
      console.error("Erro ao atualizar dados:", error);
      showMessage("Erro ao atualizar. Tente novamente.", "error");
      setFormValues(initialValues);
    } finally {
      hidePulse();
    }
  };

  const renderProfileForm = () => (
    <U.Form>
      <U.FieldRow>
        <U.Field>
          <label>Nome Completo</label>
          <input
            name="name"
            value={formValues.name}
            onChange={handleInputChange}
            disabled={!isEditing}
          />
        </U.Field>
        <U.Field>
          <label>Nome de Usuário</label>
          <input
            name="username"
            value={formValues.username}
            onChange={handleInputChange}
            disabled={!isEditing}
          />
        </U.Field>
      </U.FieldRow>
      <U.FieldRow>
        <U.Field>
          <label>Email</label>
          <input name="email" value={formValues.email} disabled={true} />
        </U.Field>
        <U.Field>
          <label>CPF / CNPJ</label>
          <input name="cpfCnpj" value={formValues.cpfCnpj} disabled={true} />
        </U.Field>
      </U.FieldRow>
      {formValues.consultorName && (
        <U.Field>
          <label>Consultor</label>
          <input
            name="consultorName"
            value={formValues.consultorName}
            disabled={true}
          />
        </U.Field>
      )}
      <U.FieldRow>
        <U.Field>
          <label>Telefone</label>
          <input
            name="phoneNumber"
            value={formValues.phoneNumber}
            onChange={handleInputChange}
            disabled={!isEditing}
          />
        </U.Field>
        <U.Field>
          <label>Profissão</label>
          <input
            name="jobTitle"
            value={formValues.jobTitle}
            onChange={handleInputChange}
            disabled={!isEditing}
          />
        </U.Field>
      </U.FieldRow>
      <U.FieldRow>
        <U.Field>
          <label>Endereço</label>
          <input
            name="address.street"
            value={formValues["address.street"]}
            onChange={handleInputChange}
            disabled={!isEditing}
          />
        </U.Field>
        <U.Field>
          <label>Número</label>
          <input
            name="address.number"
            value={formValues["address.number"]}
            onChange={handleInputChange}
            disabled={!isEditing}
          />
        </U.Field>
      </U.FieldRow>
      <U.FieldRow>
        <U.Field>
          <label>Bairro</label>
          <input
            name="address.neighborhood"
            value={formValues["address.neighborhood"]}
            onChange={handleInputChange}
            disabled={!isEditing}
          />
        </U.Field>
        <U.Field>
          <label>CEP</label>
          <input
            name="address.zipcode"
            value={formValues["address.zipcode"]}
            onChange={handleInputChange}
            disabled={!isEditing}
          />
        </U.Field>
      </U.FieldRow>
      <U.FieldRow>
        <U.Field>
          <label>Cidade</label>
          <input
            name="address.city"
            value={formValues["address.city"]}
            onChange={handleInputChange}
            disabled={!isEditing}
          />
        </U.Field>
        <U.Field>
          <label>Estado</label>
          <input
            name="address.state"
            value={formValues["address.state"]}
            onChange={handleInputChange}
            disabled={!isEditing}
          />
        </U.Field>
      </U.FieldRow>
    </U.Form>
  );

  const renderAccountForm = () => (
    <U.Form>
      <U.FieldRow>
        <U.Field>
          <label>Nome do Banco</label>
          <input
            name="bankAccount.bank"
            value={formValues["bankAccount.bank"]}
            onChange={handleInputChange}
            disabled={!isEditing}
          />
        </U.Field>
        <U.Field>
          <label>Beneficiário</label>
          <input
            name="bankAccount.beneficiary"
            value={formValues["bankAccount.beneficiary"]}
            onChange={handleInputChange}
            disabled={!isEditing}
          />
        </U.Field>
      </U.FieldRow>
      <U.FieldRow>
        <U.Field>
          <label>Agência</label>
          <input
            name="bankAccount.agencyNumber"
            value={formValues["bankAccount.agencyNumber"]}
            onChange={handleInputChange}
            disabled={!isEditing}
          />
        </U.Field>
        <U.Field>
          <label>Conta</label>
          <input
            name="bankAccount.accountNumber"
            value={formValues["bankAccount.accountNumber"]}
            onChange={handleInputChange}
            disabled={!isEditing}
          />
        </U.Field>
      </U.FieldRow>
      <U.FieldRow>
        <U.Field>
          <label>Tipo de Conta</label>
          <select
            name="bankAccount.accountType"
            value={formValues["bankAccount.accountType"]}
            onChange={handleInputChange}
            disabled={!isEditing}
          >
            <option value="Conta Corrente">Conta Corrente</option>
            <option value="Conta Poupança">Conta Poupança</option>
            <option value="Conta Pagamento">Conta Pagamento</option>
          </select>
        </U.Field>
        <U.Field>
          <label>Chave PIX</label>
          <input
            name="bankAccount.pixKey"
            value={formValues["bankAccount.pixKey"]}
            onChange={handleInputChange}
            disabled={!isEditing}
          />
        </U.Field>
      </U.FieldRow>
    </U.Form>
  );

  if (!userData) return null;

  return (
    <>
      <U.ModalOverlay onClick={() => setProfilePage(false)}>
        <U.ProfileCard onClick={(e) => e.stopPropagation()}>
          <U.Header>
            <U.BackIcon onClick={() => setProfilePage(false)} />
            <U.Title>Minha Conta</U.Title>
          </U.Header>

          <U.Content>
            <U.TabContainer>
              <U.Tab
                active={activeTab === "profile"}
                onClick={() => setActiveTab("profile")}
              >
                {" "}
                Perfil{" "}
              </U.Tab>
              <U.Tab
                active={activeTab === "account"}
                onClick={() => setActiveTab("account")}
              >
                {" "}
                Dados Bancários{" "}
              </U.Tab>
            </U.TabContainer>

            {activeTab === "profile"
              ? renderProfileForm()
              : renderAccountForm()}

            <U.Footer>
              {isEditing ? (
                <>
                  <U.Button
                    type="button"
                    className="secondary"
                    onClick={handleCancel}
                  >
                    {" "}
                    Cancelar{" "}
                  </U.Button>
                  <U.Button
                    type="button"
                    className="primary"
                    onClick={handleSubmit}
                  >
                    {" "}
                    Salvar Alterações{" "}
                  </U.Button>
                </>
              ) : (
                <U.Button
                  type="button"
                  className="primary"
                  onClick={() => setIsEditing(true)}
                >
                  {" "}
                  Editar{" "}
                  {activeTab === "profile" ? "Perfil" : "Dados Bancários"}{" "}
                </U.Button>
              )}
            </U.Footer>
          </U.Content>
        </U.ProfileCard>
      </U.ModalOverlay>
      {notification && (
        <U.Toast type={notification.type}>{notification.message}</U.Toast>
      )}
    </>
  );
};

export default ProfilePage;
